import RetroFreeDrawing from '../components/RetroFreeDrawing';
import RetroSpeedClicker from '../components/RetroSpeedClicker';
import RetroPrecisionClicker from '../components/RetroPrecisionClicker';
import RetroPixelChallenge from '../components/RetroPixelChallenge';
import RetroColorBubbles from '../components/RetroColorBubbles';
import RetroPixelDrawer from '../components/RetroPixelDrawer';
import RetroLetterCatcher from '../components/RetroLetterCatcher';

// Component mapping for game IDs
const gameComponents = {
  drawing: RetroFreeDrawing,
  speed: RetroSpeedClicker,
  precision: RetroPrecisionClicker,
  pixel: RetroPixelChallenge,
  bubbles: RetroColorBubbles,
  pixeldraw: RetroPixelDrawer,
  catcher: RetroLetterCatcher
};

// Fallback games configuration
const fallbackGames = [
  { id: 'drawing', title: 'Free Drawing', isActive: true, description: 'Draw something while you wait' },
  { id: 'speed', title: 'Speed Clicker', isActive: true, description: 'Test your clicking speed' },
  { id: 'precision', title: 'Precision Clicker', isActive: false, description: 'Click with perfect timing' },
  { id: 'pixel', title: 'Pixel Challenge', isActive: false, description: 'Match the pixel pattern' },
  { id: 'bubbles', title: 'Color Bubbles', isActive: false, description: 'Pop matching color bubbles' },
  { id: 'pixeldraw', title: 'Pixel Drawer', isActive: false, description: 'Create pixel art masterpieces' },
  { id: 'catcher', title: 'Letter Catcher', isActive: false, description: 'Catch the falling letters' }
];

// Initialize AVAILABLE_GAMES with fallback games and components
let AVAILABLE_GAMES = fallbackGames.map(game => ({
  ...game,
  component: gameComponents[game.id]
}));

// Fetch game configuration from server
let gameConfigCache = null;
let gameConfigPromise = null;

const fetchGameConfig = async () => {
  if (gameConfigCache) return gameConfigCache;
  if (gameConfigPromise) return gameConfigPromise;

  gameConfigPromise = (async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gameconfig`);
      if (!response.ok) {
        throw new Error('Failed to fetch game configuration');
      }
      const config = await response.json();
      gameConfigCache = config;
      
      // Update AVAILABLE_GAMES with fetched config
      AVAILABLE_GAMES = config.games.map(game => ({
        ...game,
        component: gameComponents[game.id]
      }));
      
      return config;
    } catch (error) {
      console.error('[RETRO-GAMESELECTOR] ❌ Error fetching game config:', error);
      // Fallback to default games if fetch fails
      const fallbackConfig = {
        games: fallbackGames
      };
      gameConfigCache = fallbackConfig;
      
      // Ensure AVAILABLE_GAMES is set to fallback
      AVAILABLE_GAMES = fallbackGames.map(game => ({
        ...game,
        component: gameComponents[game.id]
      }));
      
      return fallbackConfig;
    }
  })();

  return gameConfigPromise;
};

// Initialize the game config on module load
fetchGameConfig();

// Get available games with components attached
const getAvailableGames = async () => {
  const config = await fetchGameConfig();
  return config.games.map(game => ({
    ...game,
    component: gameComponents[game.id]
  }));
};

/**
 * Get a random game component from active games
 * @returns {Object} Game object with component and metadata
 */
export const getRandomActiveGame = () => {
  const activeGames = AVAILABLE_GAMES.filter(game => game.isActive);
  
  if (activeGames.length === 0) {
    console.warn('[RETRO] No active games found, falling back to Free Drawing');
    return AVAILABLE_GAMES[0]; // Fallback to Free Drawing
  }
  
  const randomGame = activeGames[Math.floor(Math.random() * activeGames.length)];
  

  
  return randomGame;
};

/**
 * Get all active games
 * @returns {Promise<Array>} Promise that resolves to array of active game objects
 */
export const getActiveGames = async () => {
  const games = await getAvailableGames();
  return games.filter(game => game.isActive);
};

/**
 * Get game by ID
 * @param {string} gameId - The game ID
 * @returns {Promise<Object|null>} Promise that resolves to game object or null if not found
 */
export const getGameById = async (gameId) => {
  const games = await getAvailableGames();
  return games.find(game => game.id === gameId) || null;
};

// Export the main function to get all games
export { getAvailableGames }; 